import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { logDebug, logError, logInfo } from '../utils/errorHandler';
import { updateDocumentTitleAndFavicon, resetDocumentTitleAndFavicon } from '../utils/documentTitle';
import { applyThemeColor, resetThemeColor } from '../utils/colorUtils';

/**
 * 机构信息接口
 */
export interface Organization {
  id: string;
  name: string;
  type?: string; // PUBLIC | EXECUTING
  shortName?: string;
  logoUrl?: string;
  wechatExternalDomain?: string;
  wechatAppId?: string; // 微信 AppID（用于授权）
  introduction?: string;
  status?: string; // DRAFT | ACTIVE | INACTIVE
  userAgreement?: string; // 用户协议（富文本）
  logoutAgreement?: string; // 注销协议（富文本）
  donationAgreement?: string; // 捐赠协议（富文本，仅执行机构有）
  // 证书配置
  certBackgroundImageUrl?: string; // 捐赠证书背景图URL
  certSalutation?: string; // 捐赠证书敬语
  certContentTemplate?: string; // 捐赠证书内容模板（支持占位符）
  // 主题色配置
  themeColor?: string; // 网站主题色，如 #7B61FF
  // 可以根据需要添加更多字段
}

/**
 * OrganizationContext 类型定义
 */
interface OrganizationContextType {
  organization: Organization | null;
  loading: boolean;
  error: string | null;
  isDisabled: boolean; // 机构是否被禁用
  setOrganization: (org: Organization | null) => void;
}

/**
 * 创建 Context
 */
const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

/**
 * OrganizationProvider Props
 */
interface OrganizationProviderProps {
  children: ReactNode;
}

/**
 * OrganizationProvider 组件
 * 用于在应用启动时获取当前机构信息并提供给整个应用
 */
export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // 监听 organization 状态变化
  useEffect(() => {
    console.log('[OrganizationContext] organization state changed (console.log)', {
      hasOrganization: !!organization,
      orgId: organization?.id,
      orgName: organization?.name,
      loading,
      error,
      timestamp: new Date().toISOString()
    });
    logInfo('[OrganizationContext] organization state changed', {
      hasOrganization: !!organization,
      orgId: organization?.id,
      orgName: organization?.name,
      loading,
      hasError: !!error
    });
  }, [organization, loading, error]);

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        setLoading(true);
        setError(null);

        // 获取当前域名
        let domain = window.location.hostname;
        logDebug('[OrganizationContext] Current domain', { domain, fullUrl: window.location.href });

        // 本地开发环境，可以从 localStorage 读取测试域名
        if (domain === 'localhost' || domain === '127.0.0.1') {
          logInfo('[OrganizationContext] Local development mode');
          const testDomain = localStorage.getItem('test_organization_domain');
          if (testDomain) {
            domain = testDomain;
            logInfo('[OrganizationContext] Using test domain from localStorage', { testDomain });
          }
        }

        // 统一通过域名查询机构（无论是本地开发还是生产环境）
        // 使用相对路径，自动使用当前域名
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || (window.location.origin + '/api');
        const url = `${apiBaseUrl}/organizations/by-domain?domain=${domain}`;
        logDebug('[OrganizationContext] Fetching organization', { url, domain, apiBaseUrl });
        
        const response = await fetch(url);
        
        logDebug('[OrganizationContext] Response', {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          headers: Object.fromEntries(response.headers.entries())
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          logError('[OrganizationContext] Response not OK', {
            status: response.status,
            statusText: response.statusText,
            body: errorText,
            url
          });
          throw new Error(`机构查询失败: HTTP ${response.status} ${response.statusText}\nURL: ${url}\n响应: ${errorText}`);
        }

        const result = await response.json();
        logDebug('[OrganizationContext] Response data', result);
        
        if (result.code !== 0) {
          logError('[OrganizationContext] API error', {
            code: result.code,
            message: result.message,
            fullResponse: result,
            url
          });
          throw new Error(`机构查询失败: ${result.message || '未知错误'} (code: ${result.code})\nURL: ${url}`);
        }

        logInfo('[OrganizationContext] Organization loaded', {
          id: result.data?.id,
          name: result.data?.name,
          type: result.data?.type,
          hasWechatAppId: !!result.data?.wechatAppId,
          themeColor: result.data?.themeColor
        });
        setOrganization(result.data);

        // 标题/主题仅做增强，失败也不影响展示
        try {
          updateDocumentTitleAndFavicon(result.data?.name, result.data?.logoUrl);
          if (result.data?.themeColor) {
            applyThemeColor(result.data.themeColor);
          } else {
            resetThemeColor();
          }
        } catch (e) {
          logError('[OrganizationContext] Title/theme apply failed', e);
        }
      } catch (err) {
        logError('[OrganizationContext] Failed to fetch organization', err);
        setError(err instanceof Error ? err.message : '获取机构信息失败');
        // 出错时恢复默认标题和 favicon
        resetDocumentTitleAndFavicon();
        // 出错时恢复默认主题色
        resetThemeColor();
      } finally {
        setLoading(false);
      }
    };

    fetchOrganization();
  }, []);

  // 检查机构是否被禁用
  const isDisabled = organization?.status === 'INACTIVE';

  // 监听主题色变化，动态应用
  useEffect(() => {
    if (organization?.themeColor) {
      applyThemeColor(organization.themeColor);
    } else {
      resetThemeColor();
    }
  }, [organization?.themeColor]);

  const value: OrganizationContextType = {
    organization,
    loading,
    error,
    isDisabled,
    setOrganization,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

/**
 * useOrganization Hook
 * 用于在组件中获取当前机构信息
 */
export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};

